import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ParsingRecoverySettings, { recoveryPolicy, recoveryError, serializeRecovery, recoveryModeLabel } from './ParsingRecoverySettings';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { Zap, Target, Plane, FileText, RefreshCw, Info, AlertTriangle, Cloud, Github, Loader2 } from 'lucide-react';

const SuperadminSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('ai-models');

  const [parsingFeedback, setParsingFeedback] = useState({
    s3_export_enabled: true,
    github_commit_enabled: true
  });

  const [aiSettings, setAiSettings] = useState({
    preprocess_model: 'claude-haiku-5-5',  // Cheap model for classification
    parsing_model: 'claude-sonnet-5-5',  // Main parsing model
    airline_normalization_model: 'claude-haiku-5-5',  // Fast model for airline code lookup
    smart_input_model: 'claude-haiku-5-5',  // Cheap model for smart input parsing review
    parsing_recovery: recoveryPolicy(),
    // Fallback settings
    ai_model_fallback_enabled: true,
    ai_model_fallback_model: 'claude-sonnet-5-5',
    ai_model_retry_count: 3,
    ai_model_retry_backoff_ms: 1000
  });

  // Default model costs (USD per 1M tokens) - vendor list prices as of 10/2026.
  // Must mirror get_default_model_costs() in backend/models.py.
  const DEFAULT_MODEL_COSTS = [
    // Claude - current generation
    { model_id: 'claude-sonnet-5-5', input_cost_per_1m_tokens: 2.0, output_cost_per_1m_tokens: 10.0 },
    { model_id: 'claude-haiku-5-5', input_cost_per_1m_tokens: 0.10, output_cost_per_1m_tokens: 0.50 },
    { model_id: 'claude-opus-5-5', input_cost_per_1m_tokens: 4.0, output_cost_per_1m_tokens: 20.0 },
    // Claude - legacy, still served
    { model_id: 'claude-haiku-4-5-20251001', input_cost_per_1m_tokens: 1.0, output_cost_per_1m_tokens: 5.0 },
    { model_id: 'claude-sonnet-4-6', input_cost_per_1m_tokens: 3.0, output_cost_per_1m_tokens: 15.0 },
    // Gemini - current generation (Flash prices double on 01/01/27)
    { model_id: 'gemini-3.8-flash', input_cost_per_1m_tokens: 0.75, output_cost_per_1m_tokens: 3.75 },
    { model_id: 'gemini-3.6-flash', input_cost_per_1m_tokens: 0.75, output_cost_per_1m_tokens: 3.75 },
    { model_id: 'gemini-3.5-flash-lite', input_cost_per_1m_tokens: 0.30, output_cost_per_1m_tokens: 2.50 },
  ];

  const [modelCosts, setModelCosts] = useState(DEFAULT_MODEL_COSTS);

  // Saved rates win for models they cover; defaults fill in everything else.
  const mergeModelCosts = (saved) => {
    const byId = new Map(DEFAULT_MODEL_COSTS.map(c => [c.model_id, c]));
    (saved || []).forEach(c => { if (c && c.model_id) byId.set(c.model_id, { ...byId.get(c.model_id), ...c }); });
    return Array.from(byId.values());
  };

  // All available models organized by provider. Claude Sonnet 4, Claude 3.5
  // Haiku and Gemini 2.0 Flash were retired by their vendors in 2026.
  const ALL_MODELS = {
    gemini: [
      { value: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash', description: 'Current Gemini Flash; price doubles 01/01/27', cost: '$0.75 / $3.75 per 1M tokens' },
      { value: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash', description: "Google's replacement for Gemini 2.0 Flash", cost: '$0.75 / $3.75 per 1M tokens' },
      { value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash-Lite', description: 'Cheapest current Gemini', cost: '$0.30 / $2.50 per 1M tokens' },
    ],
    claude_aliases: [
      { value: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5', description: 'Best extraction accuracy per dollar; replaces the retired Sonnet 4', cost: '$2.00 / $10.00 per 1M tokens' },
      { value: 'claude-haiku-5-5', label: 'Claude Haiku 5.5', description: 'Cheapest Claude; classification, normalization and smart input', cost: '$0.10 / $0.50 per 1M tokens' },
      { value: 'claude-opus-5-5', label: 'Claude Opus 5.5', description: 'Highest accuracy, slower and 2x the cost of Sonnet', cost: '$4.00 / $20.00 per 1M tokens' },
    ],
    claude_pinned: [
      { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (Oct 2025, legacy)', description: 'Still served; migrate to Haiku 5.5', cost: '$1.00 / $5.00 per 1M tokens' },
      { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (legacy)', description: 'Previous generation Sonnet', cost: '$3.00 / $15.00 per 1M tokens' },
    ]
  };

  // Flat list for convenience
  const ALL_MODELS_FLAT = [...ALL_MODELS.gemini, ...ALL_MODELS.claude_aliases, ...ALL_MODELS.claude_pinned];

  // Get provider from model name
  const getProviderFromModel = (modelId) => {
    if (modelId?.startsWith('claude')) return 'claude';
    if (modelId?.startsWith('gemini')) return 'gemini';
    return 'unknown';
  };

  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadSettings();
    }
  }, [user]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/system-settings`, { headers });
      setSettings(response.data);
      if (response.data.parsing_feedback) {
        setParsingFeedback(response.data.parsing_feedback);
      }
      if (response.data.ai_settings) {
        // Merge loaded settings with defaults to preserve any missing fields
        setAiSettings(prev => ({
          ...prev,
          ...response.data.ai_settings,
          parsing_recovery: recoveryPolicy(response.data.ai_settings.parsing_recovery)
        }));
        // Load model costs if available. Saved lists predate newer models, so
        // overlay them on the defaults instead of replacing the defaults;
        // otherwise new models show $0 and cannot be edited.
        if (response.data.ai_settings.model_costs) {
          setModelCosts(mergeModelCosts(response.data.ai_settings.model_costs));
        }
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (recoveryError(aiSettings.parsing_recovery)) return;
    setSaving(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const response = await axios.put(
        `${apiBase}/admin/system-settings`,
        {
          parsing_feedback: parsingFeedback,
          ai_settings: {
            ...aiSettings,
            parsing_recovery: serializeRecovery(aiSettings.parsing_recovery),
            model_costs: modelCosts
          }
        },
        { headers }
      );

      setSettings(response.data);
      setAiSettings(prev => ({ ...prev, ...response.data.ai_settings,
        parsing_recovery: recoveryPolicy(response.data.ai_settings?.parsing_recovery) }));
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const toggleParsingFeedback = (key) => {
    setParsingFeedback(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const updateModelCost = (modelId, field, value) => {
    setModelCosts(prev => {
      const exists = prev.some(cost => cost.model_id === modelId);
      const base = exists ? prev : [...prev, getModelCost(modelId)];
      return base.map(cost =>
        cost.model_id === modelId
          ? { ...cost, [field]: parseFloat(value) || 0 }
          : cost
      );
    });
  };

  const getModelCost = (modelId) => {
    return modelCosts.find(c => c.model_id === modelId) || {
      model_id: modelId,
      input_cost_per_1m_tokens: 0,
      output_cost_per_1m_tokens: 0
    };
  };

  // Check if model costs have changed
  const modelCostsChanged = () => {
    const savedCosts = settings?.ai_settings?.model_costs || DEFAULT_MODEL_COSTS;
    return modelCosts.some(cost => {
      const savedCost = savedCosts.find(c => c.model_id === cost.model_id);
      if (!savedCost) return true;
      return cost.input_cost_per_1m_tokens !== savedCost.input_cost_per_1m_tokens ||
             cost.output_cost_per_1m_tokens !== savedCost.output_cost_per_1m_tokens;
    });
  };

  const parsingFeedbackChanged = settings?.parsing_feedback &&
    (parsingFeedback.s3_export_enabled !== settings.parsing_feedback.s3_export_enabled ||
     parsingFeedback.github_commit_enabled !== settings.parsing_feedback.github_commit_enabled);

  const aiSettingsChanged = settings?.ai_settings &&
    (aiSettings.preprocess_model !== settings.ai_settings.preprocess_model ||
     aiSettings.parsing_model !== settings.ai_settings.parsing_model ||
     aiSettings.airline_normalization_model !== settings.ai_settings.airline_normalization_model ||
     aiSettings.smart_input_model !== settings.ai_settings.smart_input_model ||
     aiSettings.ai_model_fallback_enabled !== settings.ai_settings.ai_model_fallback_enabled ||
     aiSettings.ai_model_fallback_model !== settings.ai_settings.ai_model_fallback_model ||
     aiSettings.ai_model_retry_count !== settings.ai_settings.ai_model_retry_count ||
     aiSettings.ai_model_retry_backoff_ms !== settings.ai_settings.ai_model_retry_backoff_ms);

  // Also consider changes if settings don't exist yet
  const recoveryChanged = JSON.stringify(serializeRecovery(aiSettings.parsing_recovery)) !==
    JSON.stringify(serializeRecovery(recoveryPolicy(settings?.ai_settings?.parsing_recovery)));

  const hasChanges = recoveryChanged || parsingFeedbackChanged || aiSettingsChanged || modelCostsChanged() ||
    (!settings?.ai_settings && (
      aiSettings.preprocess_model !== 'claude-haiku-5-5' ||
      aiSettings.parsing_model !== 'claude-sonnet-5-5' ||
      aiSettings.airline_normalization_model !== 'claude-haiku-5-5' ||
      aiSettings.smart_input_model !== 'claude-haiku-5-5'
    ));

  if (user?.role !== 'superadmin') {
    return (
      <div className="p-6">
        <div className="text-center text-ui-red-fg">
          Access denied. Superadmin privileges required.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ui-primary"></div>
      </div>
    );
  }

  const modelLabel = (modelId) => ALL_MODELS_FLAT.find(m => m.value === modelId)?.label || modelId;

  // Model selector component
  const ModelSelector = ({ value, onChange, label, description, icon: Icon, recommended }) => {
    const groups = [
      { title: 'Google Gemini', models: ALL_MODELS.gemini },
      { title: 'Anthropic Claude — Current models', models: ALL_MODELS.claude_aliases },
      { title: 'Anthropic Claude — Legacy', models: ALL_MODELS.claude_pinned },
    ];

    return (
      <fieldset className="m-0 p-0 border-0 space-y-4">
        <legend className="flex items-start gap-3 mb-4 p-0">
          <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-ui-blue-bg text-ui-blue-fg">
            <Icon className="h-4 w-4" aria-hidden="true" />
          </span>
          <span>
            <span className="block text-sm font-semibold text-ui-ink">{label}</span>
            <span className="block text-sm text-ui-muted">{description}</span>
          </span>
        </legend>

        {groups.map(group => (
          <div key={group.title}>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-ui-muted">{group.title}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
              {group.models.map((model) => {
                const selected = value === model.value;
                return (
                  <label
                    key={model.value}
                    className={`flex items-start gap-3 rounded-xl cursor-pointer transition-colors ${
                      selected
                        ? 'border-2 border-ui-primary bg-ui-blue-bg p-[13px]'
                        : 'border border-ui-line bg-ui-surface p-3.5 hover:bg-ui-hover'
                    }`}
                  >
                    <input
                      type="radio"
                      name={label.replace(/\s/g, '_')}
                      value={model.value}
                      checked={selected}
                      onChange={(e) => onChange(e.target.value)}
                      className="mt-1 h-4 w-4 flex-none accent-ui-primary"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-ui-ink">{model.label}</span>
                        {recommended === model.value && (
                          <span className="text-xs rounded border border-ui-green-line bg-ui-green-bg px-2 py-0.5 text-ui-green-fg">Recommended</span>
                        )}
                      </span>
                      <span className="block text-xs text-ui-muted mt-0.5">{model.description}</span>
                      <span className="block text-xs text-ui-faint mt-1">{model.cost}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </fieldset>
    );
  };

  const sections = [
    { id: 'ai-models', label: 'AI models' },
    { id: 'parsing-recovery', label: 'AI parsing recovery' },
    { id: 'model-fallback', label: 'Standard fallback' },
    { id: 'model-costs', label: 'Model costs' },
    { id: 'parsing-feedback', label: 'Parsing feedback export' },
  ];

  const fieldClass = 'h-10 w-full rounded-lg border border-ui-control bg-ui-surface px-3 text-sm text-ui-ink focus:outline-none focus:ring-2 focus:ring-ui-primary/40 focus:border-ui-primary';
  const costFieldClass = 'h-8 w-24 rounded-md border border-ui-control bg-ui-surface px-2 text-sm text-ui-ink focus:outline-none focus:ring-2 focus:ring-ui-primary/40 focus:border-ui-primary';

  const renderCostRows = (models) => models.map((model) => {
    const cost = getModelCost(model.value);
    const inputId = `cost-${model.value}-input`;
    const outputId = `cost-${model.value}-output`;
    return (
      <div key={model.value} className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-sm text-ui-ink-2 w-full sm:w-56">{model.label}</span>
        <div className="flex items-center gap-2">
          <label htmlFor={inputId} className="text-xs text-ui-muted">Input<span className="sr-only"> cost for {model.label}</span>:</label>
          <input
            id={inputId}
            type="number"
            step="0.01"
            min="0"
            value={cost.input_cost_per_1m_tokens}
            onChange={(e) => updateModelCost(model.value, 'input_cost_per_1m_tokens', e.target.value)}
            className={costFieldClass}
          />
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor={outputId} className="text-xs text-ui-muted">Output<span className="sr-only"> cost for {model.label}</span>:</label>
          <input
            id={outputId}
            type="number"
            step="0.01"
            min="0"
            value={cost.output_cost_per_1m_tokens}
            onChange={(e) => updateModelCost(model.value, 'output_cost_per_1m_tokens', e.target.value)}
            className={costFieldClass}
          />
        </div>
      </div>
    );
  });

  const summary = [
    { icon: Zap, label: 'Pre-processing', value: modelLabel(aiSettings.preprocess_model) },
    {
      icon: Target,
      label: aiSettings.parsing_recovery.enabled ? 'Parsing recovery' : 'Standard parsing',
      value: aiSettings.parsing_recovery.enabled ? recoveryModeLabel(aiSettings.parsing_recovery) : modelLabel(aiSettings.parsing_model),
    },
    { icon: Plane, label: 'Airline Lookup', value: modelLabel(aiSettings.airline_normalization_model) },
    { icon: FileText, label: 'Smart Input', value: modelLabel(aiSettings.smart_input_model) },
    {
      icon: RefreshCw,
      label: 'Standard fallback',
      value: aiSettings.ai_model_fallback_enabled ? modelLabel(aiSettings.ai_model_fallback_model) : 'Disabled',
    },
  ];

  const saveDisabled = saving || !hasChanges || Boolean(recoveryError(aiSettings.parsing_recovery));

  return (
    <div className="bg-ui-page text-ui-ink min-h-full px-4 py-6 sm:px-8 sm:py-7">
      <div className="max-w-[1120px] flex flex-col gap-5">
        <header>
          <h1 className="text-2xl font-bold tracking-tight text-ui-ink">Settings</h1>
          <p className="mt-1 text-sm text-ui-muted">Configure global system behavior. Changes apply platform-wide after saving.</p>
        </header>

        {error && (
          <div role="alert" className="flex items-start gap-2 rounded-lg border border-ui-red-line bg-ui-red-bg px-4 py-3 text-sm text-ui-red-fg">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6 items-start">
          <nav aria-label="Settings sections" className="w-full lg:w-56 lg:flex-none lg:sticky lg:top-6 flex lg:flex-col gap-1 overflow-x-auto">
            {sections.map(section => {
              const active = activeSection === section.id;
              return (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  onClick={() => setActiveSection(section.id)}
                  aria-current={active ? 'location' : undefined}
                  className={`block whitespace-nowrap rounded-lg border px-3 py-2 text-sm ${
                    active
                      ? 'border-ui-line bg-ui-surface font-semibold text-ui-ink shadow-ui-card'
                      : 'border-transparent text-ui-ink-3 hover:bg-ui-hover hover:text-ui-ink'
                  }`}
                >
                  {section.label}
                </a>
              );
            })}
          </nav>

          <div className="flex-1 min-w-0 w-full flex flex-col gap-4">
            {/* AI Model Settings */}
            <SettingsCard
              id="ai-models"
              title="AI model configuration"
              description="Configure which AI models are used for document processing. Changes take effect after saving."
            >
              <div className="space-y-8">
                {/* Current Configuration Summary */}
                <div>
                  <h3 className="mb-3 text-[13px] font-semibold text-ui-ink-2">Selected configuration</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
                    {summary.map(({ icon: Icon, label, value }) => (
                      <div key={label} className="flex items-center gap-3 rounded-lg border border-ui-divider bg-ui-sunk px-3 py-2.5">
                        <Icon className="h-4 w-4 flex-none text-ui-muted" aria-hidden="true" />
                        <div className="min-w-0">
                          <span className="block text-xs text-ui-muted">{label}</span>
                          <span className="block text-sm font-medium text-ui-ink truncate">{value}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <ModelSelector
                  value={aiSettings.preprocess_model}
                  onChange={(value) => setAiSettings({ ...aiSettings, preprocess_model: value })}
                  label="Pre-processing Model"
                  description="Used for email segment classification and attachment classification. Choose a fast, cost-effective model."
                  icon={Zap}
                  recommended="claude-haiku-5-5"
                />

                <div className="pt-6 border-t border-ui-divider">
                  <ModelSelector
                    value={aiSettings.parsing_model}
                    onChange={(value) => setAiSettings({ ...aiSettings, parsing_model: value })}
                    label="Standard Parsing Model"
                    description="Used for travel document extraction when AI parsing recovery is off."
                    icon={Target}
                    recommended="claude-sonnet-5-5"
                  />
                </div>

                <div className="pt-6 border-t border-ui-divider">
                  <ModelSelector
                    value={aiSettings.airline_normalization_model}
                    onChange={(value) => setAiSettings({ ...aiSettings, airline_normalization_model: value })}
                    label="Airline Normalization Model"
                    description="Used as fallback when airline name cannot be matched deterministically. Fast, cheap model recommended since it handles simple lookups."
                    icon={Plane}
                    recommended="claude-haiku-5-5"
                  />
                </div>

                <div className="pt-6 border-t border-ui-divider">
                  <ModelSelector
                    value={aiSettings.smart_input_model}
                    onChange={(value) => setAiSettings({ ...aiSettings, smart_input_model: value })}
                    label="Smart Input Model (Parsing Review)"
                    description="Used for AI-powered smart input parsing in the review queue. Handles simple field extraction from pasted text. A fast, cheap model is recommended."
                    icon={FileText}
                    recommended="claude-haiku-5-5"
                  />
                </div>

                {/* Info box */}
                <div className="flex items-start gap-2.5 rounded-lg border border-ui-blue-line bg-ui-blue-bg px-3.5 py-3 text-[13px] leading-relaxed text-ui-blue-fg">
                  <Info className="h-4 w-4 mt-0.5 flex-none" aria-hidden="true" />
                  <div>
                    <p className="font-semibold mb-1">How it works:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Pre-processing</strong> classifies email segments and filters non-travel content before parsing</li>
                      <li><strong>Parsing</strong> extracts structured data (dates, confirmations, etc.) from travel documents</li>
                      <li><strong>Airline Normalization</strong> resolves airline names to IATA codes when deterministic matching fails (e.g., typos, unusual names)</li>
                      <li>Using cheap/fast models for pre-processing and airline lookup optimizes cost, while accurate models for parsing ensures quality</li>
                    </ul>
                  </div>
                </div>
              </div>
            </SettingsCard>

            {/* AI parsing recovery */}
            <div id="parsing-recovery" className="scroll-mt-6">
              <ParsingRecoverySettings
                value={aiSettings.parsing_recovery}
                onChange={parsing_recovery => setAiSettings(prev => ({ ...prev, parsing_recovery }))}
                saving={saving}
              />
            </div>

            {/* Fallback Configuration */}
            <SettingsCard
              id="model-fallback"
              title="Standard AI model fallback"
              description="Configure retry behavior when primary parsing model fails (truncation, network errors, etc.). When enabled, retries with a different model if primary model fails or truncates."
              action={
                <Switch
                  checked={aiSettings.ai_model_fallback_enabled}
                  onToggle={() => setAiSettings({ ...aiSettings, ai_model_fallback_enabled: !aiSettings.ai_model_fallback_enabled })}
                  label="Enable model fallback"
                />
              }
            >
              {aiSettings.ai_model_fallback_enabled ? (
                <div className="space-y-5">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="fallback-model" className="text-[13px] font-semibold text-ui-ink-2">Fallback Model</label>
                    <select
                      id="fallback-model"
                      aria-describedby="fallback-model-hint"
                      value={aiSettings.ai_model_fallback_model}
                      onChange={(e) => setAiSettings({ ...aiSettings, ai_model_fallback_model: e.target.value })}
                      className={fieldClass}
                    >
                      <optgroup label="Claude Aliases (auto-update)">
                        {ALL_MODELS.claude_aliases.map((model) => (
                          <option key={model.value} value={model.value}>
                            {model.label} - {model.description}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Claude Pinned Versions">
                        {ALL_MODELS.claude_pinned.map((model) => (
                          <option key={model.value} value={model.value}>
                            {model.label} - {model.description}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Gemini (Google)">
                        {ALL_MODELS.gemini.map((model) => (
                          <option key={model.value} value={model.value}>
                            {model.label} - {model.description}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                    <span id="fallback-model-hint" className="text-xs text-ui-muted">Model to use when primary parsing model fails</span>
                  </div>

                  {/* Retry Settings */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ui-ink-2">
                      Retry Count
                      <input
                        type="number"
                        min="0"
                        max="5"
                        value={aiSettings.ai_model_retry_count}
                        onChange={(e) => setAiSettings({ ...aiSettings, ai_model_retry_count: Math.min(5, Math.max(0, parseInt(e.target.value) || 0)) })}
                        className={fieldClass}
                      />
                      <span className="text-xs font-normal text-ui-muted">Number of retries for transient errors (0-5)</span>
                    </label>
                    <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ui-ink-2">
                      Initial Backoff (ms)
                      <input
                        type="number"
                        min="500"
                        max="5000"
                        step="100"
                        value={aiSettings.ai_model_retry_backoff_ms}
                        onChange={(e) => setAiSettings({ ...aiSettings, ai_model_retry_backoff_ms: Math.min(5000, Math.max(500, parseInt(e.target.value) || 1000)) })}
                        className={fieldClass}
                      />
                      <span className="text-xs font-normal text-ui-muted">Delay before first retry (500-5000ms)</span>
                    </label>
                  </div>

                  {/* Retry behavior explanation */}
                  <div className="rounded-lg border border-ui-divider bg-ui-sunk p-3 text-sm text-ui-ink-3">
                    <p className="font-semibold text-ui-ink-2 mb-1">Retry behavior:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>Transient errors (rate limits, timeouts) will retry up to <strong>{aiSettings.ai_model_retry_count}</strong> times with exponential backoff</li>
                      <li>Backoff sequence: {aiSettings.ai_model_retry_backoff_ms}ms → {aiSettings.ai_model_retry_backoff_ms * 2}ms → {aiSettings.ai_model_retry_backoff_ms * 4}ms</li>
                      <li>If primary model truncates or JSON parsing fails, will retry once with <strong>{modelLabel(aiSettings.ai_model_fallback_model)}</strong></li>
                    </ul>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-ui-muted">Model fallback is off. Turn it on to choose a fallback model and retry settings.</p>
              )}
            </SettingsCard>

            {/* Model Cost Configuration */}
            <SettingsCard
              id="model-costs"
              title="Model cost configuration (USD per 1M tokens)"
              description="Configure the cost per 1 million tokens for each model. Used for AI usage cost estimation."
            >
              <div className="space-y-4">
                <div className="rounded-xl border border-ui-orange-line bg-ui-orange-bg p-4">
                  <h3 className="text-sm font-semibold text-ui-orange-fg mb-3">Claude Models (Anthropic)</h3>
                  <div className="space-y-3">
                    {renderCostRows([...ALL_MODELS.claude_aliases, ...ALL_MODELS.claude_pinned])}
                  </div>
                </div>
                <div className="rounded-xl border border-ui-blue-line bg-ui-blue-bg p-4">
                  <h3 className="text-sm font-semibold text-ui-blue-fg mb-3">Gemini Models (Google)</h3>
                  <div className="space-y-3">
                    {renderCostRows(ALL_MODELS.gemini)}
                  </div>
                </div>
              </div>
            </SettingsCard>

            {/* Parsing Feedback Export Settings */}
            <SettingsCard
              id="parsing-feedback"
              title="Parsing feedback export"
              description="Configure how parsing feedback is exported for Claude Code analysis"
            >
              <div className="space-y-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <Cloud className="h-4 w-4 mt-0.5 flex-none text-ui-muted" aria-hidden="true" />
                    <div>
                      <h3 className="text-sm font-semibold text-ui-ink">S3 Export (Presigned URLs)</h3>
                      <p className="text-sm text-ui-muted mt-0.5">Export feedback with S3 presigned URLs for document access. URLs expire after 24 hours.</p>
                    </div>
                  </div>
                  <Switch
                    checked={parsingFeedback.s3_export_enabled}
                    onToggle={() => toggleParsingFeedback('s3_export_enabled')}
                    label="S3 export"
                  />
                </div>

                <div className="flex items-start justify-between gap-4 border-t border-ui-divider pt-5">
                  <div className="flex items-start gap-3">
                    <Github className="h-4 w-4 mt-0.5 flex-none text-ui-muted" aria-hidden="true" />
                    <div>
                      <h3 className="text-sm font-semibold text-ui-ink">GitHub Commit (Send to Claude Code)</h3>
                      <p className="text-sm text-ui-muted mt-0.5">Commit feedback documents directly to GitHub repository for Claude Code access. Recommended for Claude Code workflows.</p>
                    </div>
                  </div>
                  <Switch
                    checked={parsingFeedback.github_commit_enabled}
                    onToggle={() => toggleParsingFeedback('github_commit_enabled')}
                    label="GitHub commit"
                  />
                </div>

                {/* Warning if both are disabled */}
                {!parsingFeedback.s3_export_enabled && !parsingFeedback.github_commit_enabled && (
                  <div className="flex items-start gap-2.5 rounded-lg border border-ui-amber-line bg-ui-amber-bg px-3.5 py-3 text-[13px] text-ui-amber-fg">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" aria-hidden="true" />
                    <span><span className="font-semibold">Warning:</span> Both export methods are disabled. You won't be able to export parsing feedback.</span>
                  </div>
                )}
              </div>
            </SettingsCard>

            {/* Save bar */}
            <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ui-line bg-ui-sunk px-5 py-3.5 shadow-ui-card">
              <span className="text-[13px] text-ui-ink-3">
                {hasChanges ? 'Unsaved changes · takes effect after you save' : 'No unsaved changes'}
                {settings?.updated_at && (
                  <span className="block text-xs text-ui-muted">
                    Last updated: {new Date(settings.updated_at).toLocaleString()}
                    {settings.updated_by_user_email && ` by ${settings.updated_by_user_email}`}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={handleSave}
                disabled={saveDisabled}
                className="inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-lg bg-ui-primary px-4 text-sm font-semibold text-ui-on-primary hover:bg-ui-primary-hover disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-ui-primary"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Token card shell: h2 + description header, optional action (e.g. a switch) top right.
const SettingsCard = ({ id, title, description, action, children }) => (
  <section
    id={id}
    aria-labelledby={`${id}-heading`}
    className="scroll-mt-6 overflow-hidden rounded-2xl border border-ui-line bg-ui-surface shadow-ui-card"
  >
    <div className="flex items-start justify-between gap-4 border-b border-ui-divider px-5 py-5 sm:px-[22px]">
      <div>
        <h2 id={`${id}-heading`} className="text-[17px] font-bold text-ui-ink">{title}</h2>
        {description && <p className="mt-1 max-w-[62ch] text-[13px] leading-relaxed text-ui-muted">{description}</p>}
      </div>
      {action && <div className="flex-none">{action}</div>}
    </div>
    <div className="px-5 py-5 sm:px-[22px]">{children}</div>
  </section>
);

const Switch = ({ checked, onToggle, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={onToggle}
    className={`relative inline-flex h-6 w-10 flex-none items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ui-primary/40 ${
      checked ? 'bg-ui-primary' : 'bg-ui-control'
    }`}
  >
    <span
      className={`inline-block h-[18px] w-[18px] transform rounded-full bg-ui-surface shadow-ui-card transition-transform ${
        checked ? 'translate-x-[19px]' : 'translate-x-[3px]'
      }`}
    />
  </button>
);

export default SuperadminSettings;
